import { TJobForm } from "@/features/job/types/JobForm";
import { TScanCheckbox } from "@/features/job/types/ScanCheckbox";

type OptionMap = { value: string; matches: string[] };

const SALARY_TYPE_MAP: OptionMap[] = [
  { value: "monthly_salary", matches: ["月給"] },
  { value: "daily_salary", matches: ["日給"] },
  { value: "hourly_wage", matches: ["時給", "時間給"] },
];

const GENDER_MAP: OptionMap[] = [
  { value: "1", matches: ["男"] },
  { value: "2", matches: ["女"] },
  { value: "3", matches: ["性別不問"] },
];

const EXACT_MATCH_MAPS = new Set<OptionMap[]>([GENDER_MAP]);

const JAPANESE_LEVEL_MAP: OptionMap[] = [
  { value: "n1", matches: ["N1"] },
  { value: "n2", matches: ["N2"] },
  { value: "n3", matches: ["N3"] },
  { value: "n4", matches: ["N4"] },
];

const matchOption = (label: string, options: OptionMap[]): string | null => {
  const exact = EXACT_MATCH_MAPS.has(options);
  const trimmed = label.trim();
  const found = options.find((o) => o.matches.some((m) => (exact ? trimmed === m : label.includes(m))));
  return found ? found.value : null;
};

const groupMatchesField = (labels: string[], options: OptionMap[]): boolean => labels.some((l) => matchOption(l, options) !== null);

type FieldRoute = { key: "salaryType" | "gender" | "japaneseLevelType"; options: OptionMap[] };
const FIELD_ROUTES: FieldRoute[] = [
  { key: "salaryType", options: SALARY_TYPE_MAP },
  { key: "gender", options: GENDER_MAP },
  { key: "japaneseLevelType", options: JAPANESE_LEVEL_MAP },
];

const routeGroup = (labels: string[]): FieldRoute | null => FIELD_ROUTES.find((r) => groupMatchesField(labels, r.options)) ?? null;

export const mapScanCheckboxesToForm = (checkboxes: TScanCheckbox[] | undefined | null): Partial<TJobForm> => {
  if (!checkboxes || checkboxes.length === 0) return {};

  const groups = groupByGroupId(checkboxes);
  const result: Partial<TJobForm> = {};
  const leftoverLines: string[] = [];

  for (const group of groups) {
    const labels = group.map((c) => c.label);
    const firstChecked = group.find((c) => c.checked);

    const field = routeGroup(labels);
    const mapped = field && firstChecked ? matchOption(firstChecked.label, field.options) : null;

    if (field && mapped) {
      result[field.key] = mapped;
    } else {
      group.filter((c) => c.checked && c.label.trim()).forEach((c) => leftoverLines.push(c.label.trim()));
    }
  }

  if (leftoverLines.length > 0) {
    result.otherApplicationRequirements = leftoverLines.join("\n");
  }
  return result;
};

const groupByGroupId = (checkboxes: TScanCheckbox[]): TScanCheckbox[][] => {
  const byId = new Map<string, TScanCheckbox[]>();
  const singles: TScanCheckbox[][] = [];
  for (const cb of checkboxes) {
    if (!cb.groupId) {
      singles.push([cb]);
      continue;
    }
    const arr = byId.get(cb.groupId) ?? [];
    arr.push(cb);
    byId.set(cb.groupId, arr);
  }
  return [...Array.from(byId.values()), ...singles];
};
